import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';

import { RolagemDupla } from './RolagemDupla.tsx';

export function Botao({
  children,
  onClick,
  tipo = 'primario',
  submit = false,
  desabilitado = false,
  carregando = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  tipo?: 'primario' | 'secundario' | 'perigo';
  submit?: boolean;
  desabilitado?: boolean;
  carregando?: boolean;
}) {
  const estilos = {
    primario: 'bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-400',
    secundario: 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
    perigo: 'bg-red-700 text-white hover:bg-red-800 disabled:bg-red-300',
  }[tipo];

  return (
    <button
      type={submit ? 'submit' : 'button'}
      onClick={onClick}
      disabled={desabilitado || carregando}
      className={clsx(
        'inline-flex items-center gap-2 rounded px-4 py-2 text-sm font-medium transition',
        'focus:outline-2 focus:outline-offset-2 focus:outline-slate-900 disabled:cursor-not-allowed',
        estilos,
      )}
    >
      {carregando && <Loader2 className="size-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Campo({
  rotulo,
  valor,
  onChange,
  tipo = 'text',
  dica,
  obrigatorio = false,
  desabilitado = false,
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  tipo?: string;
  dica?: string;
  obrigatorio?: boolean;
  /** Campo trancado. Cinza e sem foco — a tela nunca deixa digitar o que não vai salvar. */
  desabilitado?: boolean;
}) {
  return (
    <label className="block">
      <span
        className={clsx(
          'mb-1 block text-xs font-medium tracking-wide uppercase',
          desabilitado ? 'text-slate-400' : 'text-slate-600',
        )}
      >
        {rotulo}
      </span>
      <input
        type={tipo}
        value={valor}
        required={obrigatorio}
        disabled={desabilitado}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm
                   focus:border-slate-900 focus:outline-none
                   disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400"
      />
      {dica !== undefined && <span className="mt-1 block text-xs text-slate-500">{dica}</span>}
    </label>
  );
}

export function Selecao({
  rotulo,
  valor,
  onChange,
  opcoes,
  vazio = 'Todos',
  permiteVazio = true,
  rotuloDaOpcao,
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  opcoes: string[];
  vazio?: string;
  /** Filtro aceita "todos"; escolha obrigatória, não. */
  permiteVazio?: boolean;
  /** Quando o valor gravado não é o texto que se mostra. */
  rotuloDaOpcao?: (valor: string) => string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium tracking-wide text-slate-600 uppercase">
        {rotulo}
      </span>
      <select
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm
                   focus:border-slate-900 focus:outline-none"
      >
        {permiteVazio && <option value="">{vazio}</option>}
        {opcoes.map((o) => (
          <option key={o} value={o}>
            {rotuloDaOpcao === undefined ? o : rotuloDaOpcao(o)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Cartao({
  titulo,
  descricao,
  acoes,
  children,
}: {
  titulo?: string;
  descricao?: string;
  acoes?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded border border-slate-200 bg-white">
      {(titulo !== undefined || acoes !== undefined) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            {titulo !== undefined && <h2 className="font-semibold text-slate-900">{titulo}</h2>}
            {descricao !== undefined && (
              <p className="mt-0.5 text-sm text-slate-500">{descricao}</p>
            )}
          </div>
          {acoes}
        </header>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Aviso({
  tom = 'atencao',
  titulo,
  children,
}: {
  tom?: 'atencao' | 'erro' | 'info';
  titulo?: string;
  children: ReactNode;
}) {
  const cores = {
    atencao: 'border-amber-300 bg-amber-50 text-amber-900',
    erro: 'border-red-300 bg-red-50 text-red-900',
    info: 'border-sky-300 bg-sky-50 text-sky-900',
  }[tom];
  const Icone = tom === 'info' ? Info : AlertTriangle;

  return (
    <div className={clsx('flex gap-3 rounded border p-3 text-sm', cores)}>
      <Icone className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">
        {titulo !== undefined && <p className="font-semibold">{titulo}</p>}
        <div className="space-y-1">{children}</div>
      </div>
    </div>
  );
}

export function Carregando({ texto = 'Carregando…' }: { texto?: string }) {
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
      <Loader2 className="size-4 animate-spin" />
      {texto}
    </div>
  );
}

export function Etiqueta({ children, tom = 'neutro' }: { children: ReactNode; tom?: string }) {
  const cores: Record<string, string> = {
    neutro: 'bg-slate-100 text-slate-700',
    bom: 'bg-emerald-100 text-emerald-800',
    ruim: 'bg-red-100 text-red-800',
    medio: 'bg-amber-100 text-amber-800',
  };
  return (
    <span
      className={clsx(
        'inline-block rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        cores[tom] ?? cores.neutro,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Tabela com rolagem horizontal própria, para o corpo da página nunca rolar de
 * lado. `rolagemNoTopo` acrescenta uma segunda barra acima do cabeçalho — vale
 * a pena quando a tabela é longa e a barra de baixo fica longe demais.
 */
export function Tabela({
  cabecalho,
  children,
  rolagemNoTopo = false,
}: {
  cabecalho: ReactNode;
  children: ReactNode;
  rolagemNoTopo?: boolean;
}) {
  const tabela = (
    <table className="w-full text-sm">
      <thead className="border-b border-slate-300 text-left text-xs tracking-wide text-slate-500 uppercase">
        {cabecalho}
      </thead>
      <tbody className="divide-y divide-slate-100">{children}</tbody>
    </table>
  );

  if (rolagemNoTopo) return <RolagemDupla>{tabela}</RolagemDupla>;
  return <div className="overflow-x-auto">{tabela}</div>;
}
